import queryString, { ParsedQuery, ParseOptions } from 'query-string';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

interface useQueryStringStateOptions {
  isCommon: boolean;
}

const parseOptions: ParseOptions = {
  parseNumbers: true,
  parseBooleans: true,
};

export function useQueryStringState<T>(keyName: string, initialState?: undefined): [T | undefined, (state: T | undefined) => void];

export function useQueryStringState<T>(keyName: string, initialState: T): [T, (state: T) => void];

export function useQueryStringState<T>(
  keyName: string,
  initialState: T | undefined,
  options?: useQueryStringStateOptions,
): [T | undefined, (state: T | undefined) => void] {
  const isCommon = !!(options && options.isCommon);

  const id = useRef<string | undefined>(
    !isCommon
      ? Math.random()
          .toString(36)
          .substr(2, 8)
      : undefined,
  );
  const location = useLocation();
  const history = useHistory();

  const getValueFromQueryString = useCallback(() => {
    const obj: ParsedQuery<unknown> = queryString.parse(location.search, parseOptions);

    return obj[keyName] as T;
  }, [keyName, location.search]);

  const [state, setState] = useState<T | undefined>(getValueFromQueryString() || initialState);

  const setQueryString = useCallback(
    (value: T | undefined) => {
      const params = {
        ...queryString.parse(window.location.search, parseOptions),
        [keyName]: value,
      };

      history.push(`?${queryString.stringify(params)}`, { id });
    },
    [keyName, history],
  );

  const setStateWithQueryString = useCallback(
    (value: T | undefined) => {
      setState(value);
      setQueryString(value);
    },
    [setQueryString],
  );

  useEffect(() => {
    const val = getValueFromQueryString();

    if (!isCommon && id.current !== (location.state as any)?.id.current) {
      return;
    }

    setState(ps => val || ps || initialState);
  }, [getValueFromQueryString, initialState, location.state, isCommon]);

  return [state, setStateWithQueryString];
}
